import React, { useMemo, useState } from 'react';
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import {
  getDayDateTimeFromEntryDateTitle,
  getDayMonthYearFromDate,
  daysAhead,
  getThreeLetterMonthFromIndex
} from '../../util/dateTime';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { Nugget, Entry, CATEGORY_LABELS, areNuggets } from '../../views/retrospective/types';
import JumbleModal from '../jumbleModal/JumbleModal';
import { loadExistingEntry } from '../calendar/calendar';
import { EntryStates, isEntryStates } from '../calendar/types';
import { useHistory } from 'react-router-dom';
import { useGeneralContext } from '../../context';
import { DayMonthYear } from './types';

/*
 * Day-cell geometry. `md` is the original touch-sized cell; `sm` is small
 * enough that a full year of week columns fits the home page's content column.
 * The pixel values are exported because the hook that decides how many weeks
 * fit has to agree with what is actually drawn.
 */
export const CELL_SIZE_PX = { sm: 16, md: 24 } as const;
export type CellSize = keyof typeof CELL_SIZE_PX;
const CELL_CLASS: Record<CellSize, { box: string; height: string }> = {
  sm: { box: 'tw-h-4 tw-w-4', height: 'tw-h-4' },
  md: { box: 'tw-h-6 tw-w-6', height: 'tw-h-6' }
};

/** The Mon/Wed/Fri gutter plus the gap after it, for the fits-in-container sum. */
export const WEEKDAY_GUTTER_PX = 36;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Labelling every row is noise; GitHub labels three and so do we. */
const LABELLED_WEEKDAYS = [1, 3, 5];

/*
 * A day with entries used to be one flat colour, so a day you scribbled a line
 * on looked the same as a day you wrote three times. The shading now steps with
 * the number of entries on the day.
 */
const LEVEL_CLASS = ['tw-bg-neutral-100', 'tw-bg-primary-300', 'tw-bg-primary-500', 'tw-bg-primary-700'];
const levelForEntryCount = (count: number) => Math.min(count, LEVEL_CLASS.length - 1);

/** The key to the shading above. Lives here so it cannot drift from it. */
export const GridLegend = () => {
  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-neutral-500">
      <span>No entry</span>
      {LEVEL_CLASS.map((levelClass) => (
        <div key={levelClass} className={`tw-h-3 tw-w-3 tw-rounded ${levelClass}`} aria-hidden="true"></div>
      ))}
      <span>3+ a day</span>
    </div>
  );
};

const WeekdayLabels = (props: { cellSize: CellSize }) => {
  return (
    <div className="tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-1">
      {/* matches the month-label row every week column starts with */}
      <div className="tw-h-4 tw-mb-1"></div>
      {WEEKDAYS.map((weekday, i) => (
        <div
          key={weekday}
          className={`tw-w-8 ${CELL_CLASS[props.cellSize].height} tw-flex tw-items-center tw-text-xs tw-leading-none tw-text-neutral-500`}
        >
          {LABELLED_WEEKDAYS.includes(i) ? weekday : ''}
        </div>
      ))}
    </div>
  );
};

export const WeeklyColumnsGrid = (props: {
  gridValues: (Nugget | Entry)[];
  dateRangeSelectorOnTop?: true;
  tagFilter: string[];
  handleClick?: () => void;
  range: { start: DayMonthYear; end: DayMonthYear };
  setRange: React.Dispatch<React.SetStateAction<{ start: DayMonthYear; end: DayMonthYear }>>;
  weeksToDisplay: number;
  cellSize?: CellSize;
  showWeekdayLabels?: boolean;
  /** Given, every empty past day becomes a button that writes an entry for it. */
  onEmptyDayClick?: (date: Date) => void;
}) => {
  const {
    gridValues,
    handleClick,
    dateRangeSelectorOnTop,
    tagFilter,
    range,
    setRange,
    weeksToDisplay,
    showWeekdayLabels,
    onEmptyDayClick
  } = props;
  const cellSize: CellSize = props.cellSize || 'md';

  const [modalContent, setModalContent] = useState<EntryStates>([]);
  const [itemLoading, setItemLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const columns = [];
  let columnStartDate = new Date(range.start.year, range.start.month, range.start.day);
  const columnStartDateDayOfWeek = columnStartDate.getDay();
  let columnEndDate = daysAhead(7 - columnStartDateDayOfWeek, columnStartDate);

  const updateRange = (direction: 'fwd' | 'back') => {
    if (direction === 'fwd') {
      const newStart = new Date(range.end.year, range.end.month, range.end.day);
      newStart.setDate(newStart.getDate() + 1);
      const newEnd = new Date(newStart.getTime());
      newEnd.setDate(newEnd.getDate() + weeksToDisplay * 7);
      setRange({
        start: getDayMonthYearFromDate(newStart),
        end: getDayMonthYearFromDate(newEnd)
      });
      return;
    }
    // back
    const newEnd = new Date(range.start.year, range.start.month, range.start.day);
    newEnd.setDate(newEnd.getDate() - 1);
    const newStart = new Date(newEnd.getTime());
    newStart.setDate(newStart.getDate() - weeksToDisplay * 7);
    setRange({
      start: getDayMonthYearFromDate(newStart),
      end: getDayMonthYearFromDate(newEnd)
    });
  };

  const endRangeIsCurrent = () => {
    const today = new Date();
    const { day, month, year } = range.end;
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDayOfMonth = today.getDate();
    return (
      year > currentYear ||
      (year === currentYear && month > currentMonth) ||
      (year === currentYear && month === currentMonth && day >= currentDayOfMonth)
    );
  };

  const GridHeader = () => {
    return (
      <div
        className={`tw-w-full tw-flex tw-flex-row tw-justify-start tw-items-center tw-gap-1 tw-font-semibold tw-text-sm ${
          dateRangeSelectorOnTop ? 'tw-mb-2' : 'tw-mt-2'
        }`}
      >
        <button
          aria-label="Previous weeks"
          className="tw-flex tw-items-center tw-justify-center tw-min-h-touch-min tw-min-w-touch-min tw-bg-transparent tw-border-none tw-cursor-pointer"
          onClick={() => {
            updateRange('back');
          }}
        >
          <ChevronLeft size={18} />
        </button>
        {`${getThreeLetterMonthFromIndex(range.start.month)} ${range.start.year} - ${getThreeLetterMonthFromIndex(
          range.end.month
        )} ${range.end.year}`}
        <button
          disabled={endRangeIsCurrent()}
          aria-label="Next weeks"
          className="tw-flex tw-items-center tw-justify-center tw-min-h-touch-min tw-min-w-touch-min tw-bg-transparent tw-border-none tw-cursor-pointer disabled:tw-opacity-40 disabled:tw-cursor-default"
          onClick={() => {
            updateRange('fwd');
          }}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    );
  };

  for (let j = 0; j <= weeksToDisplay; j++) {
    columns.push(
      <WeekColumn
        weekIndex={j}
        key={`${columnStartDate.getTime()}-${j}`}
        gridValues={gridValues}
        tagFilter={tagFilter}
        start={getDayMonthYearFromDate(columnStartDate)}
        gridEnd={range.end}
        setShowModal={setShowModal}
        setModalContent={setModalContent}
        handleClick={handleClick}
        itemLoading={itemLoading}
        setItemLoading={setItemLoading}
        cellSize={cellSize}
        onEmptyDayClick={onEmptyDayClick}
      />
    );
    columnStartDate = new Date(columnEndDate.getTime());
    columnEndDate = daysAhead(7, columnEndDate);
  }
  return (
    <>
      {dateRangeSelectorOnTop && <GridHeader />}
      <div className="tw-flex tw-flex-row tw-justify-start tw-items-start tw-gap-1">
        <JumbleModal
          showModal={showModal}
          closeModal={() => setShowModal(false)}
          modalSections={[() => getModalContent(modalContent)]}
        />
        {showWeekdayLabels && <WeekdayLabels cellSize={cellSize} />}
        {columns}
      </div>
      {!dateRangeSelectorOnTop && <GridHeader />}
    </>
  );
};

const WeekColumn = (props: {
  weekIndex: number;
  gridValues: (Nugget | Entry)[];
  start: DayMonthYear;
  gridEnd: DayMonthYear;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setModalContent: React.Dispatch<React.SetStateAction<EntryStates>>;
  itemLoading: boolean;
  setItemLoading: React.Dispatch<React.SetStateAction<boolean>>;
  tagFilter: string[];
  handleClick?: () => void;
  cellSize: CellSize;
  onEmptyDayClick?: (date: Date) => void;
}) => {
  const {
    weekIndex,
    gridValues,
    start,
    gridEnd,
    setShowModal,
    setModalContent,
    itemLoading,
    setItemLoading,
    tagFilter,
    handleClick,
    cellSize,
    onEmptyDayClick
  } = props;
  const Heading = (props: { title: string }) => {
    return (
      <div className="tw-w-6 tw-h-4 tw-mb-1 tw-text-left tw-text-xs tw-leading-4 tw-whitespace-nowrap tw-overflow-visible tw-text-neutral-500">
        {props.title}
      </div>
    );
  };
  const column =
    start.day === 1
      ? [<Heading key={`header-${weekIndex}`} title={getThreeLetterMonthFromIndex(start.month)} />]
      : [<Heading key={`header-${weekIndex}`} title="" />];
  // sunday is 0
  const startDate = new Date(start.year, start.month, start.day);
  // always 0 (sunday) except on the first column which may start later in the week
  const startDayOfWeek = startDate.getDay();
  const startMonth = start.month;
  const today = getDayMonthYearFromDate(new Date());
  for (let i = 0; i < 7; i++) {
    const indexDate = daysAhead(i - startDayOfWeek, startDate);
    const { year, month, day } = getDayMonthYearFromDate(indexDate);
    const newMonthReachedInThisColumn = i === 6 && month !== startMonth;
    if (newMonthReachedInThisColumn) {
      column[0] = <Heading key={`header-${weekIndex}-${i}`} title={getThreeLetterMonthFromIndex(month)} />;
    }
    const renderingBoxIsInTheFuture =
      today.year < year ||
      (today.year === year && today.month < month) ||
      (today.year === year && today.month === month && day > today.day);
    if (i < startDayOfWeek) {
    }

    // the first column may be incomplete, rather than starting on sunday
    if (i < startDayOfWeek) {
      column.push(<DayBoxPlaceHolder key={`placeholderbox-${weekIndex}-${i}`} cellSize={cellSize} />);
      continue;
    }
    if (renderingBoxIsInTheFuture) {
      column.push(<DayBoxPlaceHolder key={`placeholderbox-${weekIndex}-${i}`} cellSize={cellSize} />);
      continue;
    }

    // when the user scrolls back, the ends at an "arbitrary" cutoff date
    const gridEndDate = new Date(gridEnd.year, gridEnd.month, gridEnd.day);
    if (indexDate.getTime() > gridEndDate.getTime()) {
      column.push(<DayBoxPlaceHolder key={`placeholderbox-${weekIndex}-${i}`} cellSize={cellSize} />);
      continue;
    }

    const nugs = gridValues.filter(
      (gridValue) =>
        Number(gridValue.entryName.slice(0, 4)) === year &&
        Number(gridValue.entryName.slice(4, 6)) === month + 1 &&
        Number(gridValue.entryName.slice(6, 8)) === day
    );
    if (nugs.length === 0) {
      column.push(
        <DayBoxEmpty
          key={`emptybox-${weekIndex}-${i}`}
          day={day}
          month={month + 1}
          year={year}
          cellSize={cellSize}
          onClick={onEmptyDayClick && (() => onEmptyDayClick(indexDate))}
        />
      );
      continue;
    }
    column.push(
      <DayBox
        key={`daybox-${weekIndex}-${i}`}
        gridValues={nugs}
        tagFilter={tagFilter}
        day={day}
        setShowModal={setShowModal}
        setModalContent={setModalContent}
        handleClick={handleClick}
        itemLoading={itemLoading}
        setItemLoading={setItemLoading}
        cellSize={cellSize}
      />
    );
  }
  return (
    <div
      key={`${start.year}${start.month}${start.day}`}
      className="tw-flex tw-flex-col tw-justify-start tw-items-center tw-gap-1"
    >
      {column}
    </div>
  );
};

const DayBox = (props: {
  gridValues: (Nugget | Entry)[];
  day: number;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setModalContent: React.Dispatch<React.SetStateAction<EntryStates>>;
  itemLoading: boolean;
  setItemLoading: React.Dispatch<React.SetStateAction<boolean>>;
  tagFilter: string[];
  handleClick?: () => void;
  cellSize: CellSize;
}) => {
  const {
    gridValues,
    day,
    setShowModal,
    setModalContent,
    handleClick,
    itemLoading,
    setItemLoading,
    tagFilter,
    cellSize
  } = props;
  const cellClass = CELL_CLASS[cellSize].box;
  const context = useGeneralContext();
  const { nuggets } = context;
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const allNugTags = useMemo(() => {
    return nuggets
      .filter((nug) => gridValues.map((v) => v.entryName).includes(nug.entryName))
      .map((n) => n.tags)
      .flat();
  }, [gridValues, nuggets]);
  if (areNuggets(gridValues)) {
    const popover = getPopover({
      nugs: gridValues,
      setLoading,
      setItemLoading,
      setShowModal,
      setModalContent,
      hidePopover: () => setShowPopover(false)
    });

    return (
      <OverlayTrigger
        overlay={popover}
        delay={0}
        trigger="click"
        rootClose
        show={showPopover}
        onToggle={(nextShow) => setShowPopover(nextShow)}
      >
        <button
          key={day}
          className={`${cellClass} tw-rounded tw-border-none ${loading ? 'tw-animate-pulse' : ''}`}
          style={{ backgroundColor: getColorFromNugs(gridValues) || 'grey' }}
        ></button>
      </OverlayTrigger>
    );
  }
  const { day: weekday, date } = getDayDateTimeFromEntryDateTitle(gridValues[0].entryName);

  const allTags = [...gridValues.map((gridValue) => gridValue.tags).flat(), ...allNugTags];
  const isHiddenByTagFilter = tagFilter.length > 0 && tagFilter.some((tag) => !allTags.includes(tag));
  return (
    <OverlayTrigger
      overlay={
        <Tooltip id="grid-day-tooltip">{`${weekday}, ${date} · ${gridValues.length} ${gridValues.length === 1 ? 'entry' : 'entries'}`}</Tooltip>
      }
      delay={0}
      placement="top"
    >
      {
        <button
          disabled={itemLoading}
          key={day}
          onClick={async () => {
            try {
              setLoading(true);
              setItemLoading(true);
              handleClick && handleClick();
              const entryStates = [];
              for (const gridValue of gridValues) {
                const le = await loadExistingEntry({
                  entryName: gridValue.entryName
                });

                entryStates.push({
                  ...le,
                  name: gridValue.entryName
                });
              }
              if (!isEntryStates(entryStates)) {
                const error = `[CalendarMobileView] Invalid entry stats: ${JSON.stringify(entryStates)}`;
                console.error(error);
                throw new Error(error);
              }
              setItemLoading(true);
              setLoading(false);
              return history.push({
                pathname: `/entry/${gridValues[0].entryName}`,
                state: { entryState: entryStates }
              });
            } catch (error) {
              console.error(error);
              setItemLoading(true);
              setLoading(false);
            }
          }}
          className={`${cellClass} tw-rounded tw-border-none tw-cursor-pointer ${
            isHiddenByTagFilter ? 'tw-bg-primary-100' : LEVEL_CLASS[levelForEntryCount(gridValues.length)]
          } ${loading ? 'tw-animate-pulse' : ''}`}
        ></button>
      }
    </OverlayTrigger>
  );
};

const DayBoxPlaceHolder = (props: { cellSize: CellSize }) => {
  return (
    <div
      key={'placeholder'}
      className={`${CELL_CLASS[props.cellSize].box} tw-rounded tw-border tw-border-solid tw-border-black tw-opacity-0`}
      style={{ backgroundColor: 'white' }}
    ></div>
  );
};

const DayBoxEmpty = (props: { day: number; month: number; year: number; cellSize: CellSize; onClick?: () => void }) => {
  const { cellSize, onClick } = props;
  const { day, date } = getDayDateTimeFromEntryDateTitle(
    `${props.year}${props.month.toString().padStart(2, '0')}${props.day.toString().padStart(2, '0')}000000000`
  );
  const cellClass = CELL_CLASS[cellSize].box;

  /*
   * An empty day used to be a dead `div`: the one thing you might want to do
   * with a day you did not write on had no affordance at all. Entry names are
   * timestamps, so writing one for a past day is just a past timestamp.
   */
  return (
    <OverlayTrigger
      placement="top"
      delay={0}
      overlay={
        <Tooltip id="grid-empty-day-tooltip">
          {onClick ? `Write an entry for ${day}, ${date} · empty` : `${day}, ${date}`}
        </Tooltip>
      }
    >
      {onClick ? (
        <button
          key={day}
          aria-label={`Write an entry for ${day}, ${date}`}
          onClick={onClick}
          className={`${cellClass} tw-rounded tw-border-none tw-bg-neutral-100 tw-cursor-pointer hover:tw-bg-primary-200`}
        ></button>
      ) : (
        <div key={day} className={`${cellClass} tw-rounded tw-border-none tw-bg-neutral-100`}></div>
      )}
    </OverlayTrigger>
  );
};

const getModalContent = (entryStates: EntryStates) => {
  const { day, date, time } = getDayDateTimeFromEntryDateTitle(entryStates[0].name);
  const allSameEntry = entryStates.every((entryState) => entryState.name === entryStates[0].name);
  const dateString = entryStates.length === 1 || allSameEntry ? `${day}, ${date} at ${time}` : `${day}, ${date}`;
  const content = entryStates.map((entryState) => {
    return (
      <div className="tw-text-base tw-mb-4">
        <div className="tw-inline">{`${entryState.blocks
          .filter((block) => block.role === 'user')
          .map((block) => `${block.content}\n\n`)}`}</div>
      </div>
    );
  });
  const popover = (
    <div>
      <div className="tw-text-lg tw-font-semibold tw-mb-4">{dateString}</div>
      {content}
    </div>
  );
  return popover;
};

const getXPostButton = (nugs: Nugget[], allSameEntry: boolean) => {
  const tweetText = nugs
    .filter((_v, i) => i < 5)
    .map((nug) => {
      return nug.content;
    })
    .join('\n\n');

  const truncatedTweetText = tweetText.length > 215 ? tweetText.slice(0, 215) + '...' : tweetText;

  return (
    <OverlayTrigger
      placement="top"
      delay={0}
      overlay={<Tooltip id="x-post-tooltip">Share your wisdom with the world 🌍</Tooltip>}
    >
      <a
        target="_blank"
        rel="noreferrer"
        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
          `${truncatedTweetText}${tweetText.length > 215 ? '...' : ''}\n\n✍️ finding clarity @JumbleJournal`
        )}&url=https://jumblejournal.org?lang=en`}
        className="tw-relative tw-h-5 tw-w-[73px] tw-box-border tw-py-1 tw-px-3 tw-bg-black tw-text-white tw-rounded-full tw-font-medium tw-cursor-pointer tw-no-underline tw-flex tw-items-center tw-font-['Helvetica_Neue',Arial,sans-serif] tw-text-xs tw-leading-[18px] hover:tw-bg-[#333333] hover:tw-text-white hover:tw-no-underline"
      >
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 50 50" fill="white">
            <path d="M11 4C7.134 4 4 7.134 4 11v28c0 3.866 3.134 7 7 7h28c3.866 0 7-3.134 7-7V11c0-3.866-3.134-7-7-7H11zm2.086 9h7.938l5.637 8.01L33.5 13h2.5l-8.211 9.613L37.914 37h-7.936L23.438 27.707 15.5 37H13l9.309-10.896L13.086 13zm3.829 2l14.107 20h3.064l-14.107-20h-3.064z" />
          </svg>
          Post
        </div>
      </a>
    </OverlayTrigger>
  );
};

const getPopover = (props: {
  nugs: Nugget[];
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setItemLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setModalContent: React.Dispatch<React.SetStateAction<EntryStates>>;
  hidePopover?: () => void;
}) => {
  const { nugs, setLoading, setItemLoading, setShowModal, setModalContent, hidePopover } = props;
  const { day, date, time } = getDayDateTimeFromEntryDateTitle(nugs[0].entryName);
  const allSameEntry = nugs.every((nug) => nug.entryName === nugs[0].entryName);
  const dateString = nugs.length === 1 || allSameEntry ? `${day}, ${date} at ${time}` : `${day}, ${date}`;
  const content = nugs
    .filter((_v, i) => i < 5)
    .map((nug, i) => {
      const { time } = getDayDateTimeFromEntryDateTitle(nug.entryName);
      return (
        <div key={`${i}-popover-content-key`}>
          <strong>{allSameEntry ? nug.category : `${time} - ${nug.category}`}</strong>: {nug.content}
        </div>
      );
    });

  if (nugs.length > 5) {
    content.push(
      <div key={`popover-content-overflow`} className="tw-mt-4">
        <strong>Click the box for more detail</strong>
      </div>
    );
  }

  const popover = (
    <Popover key={nugs[0].entryName} id="popover-basic">
      <Popover.Header key={nugs[0].entryName + '-1'} as="h3" className="tw-flex tw-justify-between tw-items-center">
        {dateString}
        <div
          className="tw-cursor-pointer hover:tw-opacity-70"
          onClick={async () => {
            try {
              setLoading(true);
              setItemLoading(true);
              const entryStates = [];
              const le = await loadExistingEntry({
                entryName: nugs[0].entryName
              });
              entryStates.push({
                ...le,
                name: nugs[0].entryName
              });
              if (!isEntryStates(entryStates)) {
                const error = `Invalid entry stats: ${JSON.stringify(entryStates)}`;
                console.error(error);
                throw new Error(error);
              }
              setItemLoading(true);
              setLoading(false);
              setModalContent(entryStates);
              setShowModal(true);
              hidePopover?.();
            } catch (error) {
              console.error(error);
              setItemLoading(true);
              setLoading(false);
            }
          }}
        >
          <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 4h4m0 0v4m0-4-5 5M8 20H4m0 0v-4m0 4 5-5"
            />
          </svg>
        </div>
      </Popover.Header>
      <Popover.Body key={nugs[0].entryName + '-2'}>
        {content.length > 100 ? content.slice(0, 100) + '...' : content}
        <div className="tw-mt-4 tw-flex tw-justify-end tw-items-center">{getXPostButton(nugs, allSameEntry)}</div>
      </Popover.Body>
    </Popover>
  );
  return popover;
};

const getColorFromNugs = (nugs: Nugget[]) => {
  if (nugs.length === 0) {
    return 'grey';
  }
  // if (nugs.length > 1) {
  //   return '#FFD700'; // gold
  // }
  return getCategoryColorFromCategoryKey(nugs[0].category);
};

const getCategoryColorFromCategoryKey = (key: string) => {
  return CATEGORY_LABELS.find((l) => l.key === key)?.color;
};
